import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { isNil } from 'es-toolkit';
import { print as gqlDocToQueryString } from 'graphql';

import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';

type CachelessData<TData> = {
  data: TData | undefined;
  error: Error | undefined;
  loading: boolean;
};

export function useQueryWithoutCaching<TVariables extends OperationVariables, TData>(
  queryDocumentNode: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): CachelessData<TData> {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TData | undefined>();
  const [error, setError] = useState<Error | undefined>();

  LocalStorage.getItem('accessToken')
    .then((authToken) =>
      fetch(`${apiHost}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: !isNil(authToken) ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({
          query: gqlDocToQueryString(queryDocumentNode),
          variables,
        }),
      }),
    )
    .then((response) => response.json())
    .then((data: { data: TData }) => {
      setData(data.data);
    })
    .catch((error: unknown) => {
      setError(error instanceof Error ? error : new Error(String(error)));
    })
    .finally(() => setLoading(false));
  return { data, error, loading };
}

async function postToGraphQL<TVariables extends OperationVariables, TData>(
  method: 'query' | 'mutation',
  queryDocumentNode: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): Promise<TData | undefined> {
  const authToken = await LocalStorage.getItem('accessToken');

  const response = await fetch(`${apiHost}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: !isNil(authToken) ? `Bearer ${authToken}` : '',
    },
    body: JSON.stringify({
      [method]: gqlDocToQueryString(queryDocumentNode),
      variables,
    }),
  });

  const result: { data: TData } = await response.json();
  return result.data;
}

export async function fetchQueryWithoutCaching<TVariables extends OperationVariables, TData>(
  queryDocumentNode: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): Promise<TData | undefined> {
  return await postToGraphQL('query', queryDocumentNode, variables);
}

export async function postMutationWithoutCaching<TVariables extends OperationVariables, TData>(
  mutationDocumentNode: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): Promise<TData | undefined> {
  return await postToGraphQL('mutation', mutationDocumentNode, variables);
}
